import React, { FC, useContext, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { CREATE_MATTER, CREATE_CLIENT } from "../graphql/mutation";
import {
  FILTERED_CLIENTS_QUERY,
  FILTER_MATTERS_QUERY,
  ORGANISATIONAL_UNITS_QUERY,
} from "../graphql/query";

import {
  createClient,
  createClientVariables,
  ExternalMatterStatus,
  FindFilteredClients,
  FindFilteredClientsVariables,
  MatterStatus,
} from "../graphql/types";
import {
  ClientFilterData,
  ClientValue,
  CreateMatter,
  MatterCreationOrEditingForm,
  MatterFormMode,
  MatterStartingOptions,
  OnClientSave,
  StartingPointSelection,
  TemplateSelectionForm,
} from "@design-system";
import { Tabs } from "antd";
import { ClientTemplateTabContainer } from "../containers/client-template-tab-container";
import { PracticeAreaTemplateTabContainer } from "../containers/practice-area-template-tab-container";
import { useFilteredClientsLazy } from "../hooks/clients";
import { useExternalMattersQueryLazy } from "../hooks/external-matters";
import { useMutation, useSettings } from "../hooks";
import { FeeEarnerCascaderContainer } from "../containers/fee-earner-cascader-container";
import { FindOrganisationalUnits } from "../graphql/__generated_types__/FindOrganisationalUnits";
import { GlobalContext } from "../context/global-context";
import { FIND_PARENT_MATTER_QUERY } from "~/containers/parent-matter/parent-matter-query";
import { FilteredMattersInput } from "~/gql/graphql";
import { CreateMultiDisc } from "~/design-system/components/create-multi-disc/CreateMultiDisc";
import { useCreateMultiDisc } from "~/graphql/mutation/create-parent-matter";
import { VPDLabel } from "~/context/taxonomy-context";
const baseVariables: FilteredMattersInput = {
  filter: {
    status: [MatterStatus.Draft],
    template: false,
  },
  orderBy: [
    {
      updatedAt: "desc",
    },
  ],
  offset: 0,
  limit: 10,
};

export const CreateMatterButton: FC<{
  parentMatterId?: number;
  labelOveride?: string;
  client?: ClientValue;
}> = ({ parentMatterId, labelOveride, client }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentUserInfo = useContext(GlobalContext);
  const variables = {
    input: {
      ...baseVariables,
      filter: {
        ...baseVariables.filter,
        ...(currentUserInfo?.currentUser?.userId && {
          createdById: { in: [currentUserInfo?.currentUser.userId] },
        }),
      },
    },
  };

  const PA_LABEL = VPDLabel("PracticeArea");
  const TEMPLATE_LABEL = VPDLabel("Template");

  const { createMultiDiscMatter } = useCreateMultiDisc();

  const [createMatter, { loading: loadingCreateMatter }] = useMutation(CREATE_MATTER, {
    refetchQueries: (data) => {
      if (data.data?.createMatter.parent?.id) {
        return [
          {
            query: FIND_PARENT_MATTER_QUERY,
            variables: { id: data.data.createMatter.parent?.id },
          },
        ];
      }
      return [];
    },
    onCompleted: (data) => {
      if (data.createMatter.parent?.id && !pathname.includes("parent")) {
        return navigate(`/parent/${data.createMatter.parent?.id}`, { replace: true });
      }
      return navigate(`/matter-editor/${data.createMatter.id}`);
    },
    update: (cache, createdMatter) => {
      if (!createdMatter?.data) return;
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredMatters",
      });
      cache.updateQuery(
        {
          query: FILTER_MATTERS_QUERY,
          variables,
        },
        (currentData) => {
          if (!currentData) return currentData;
          const currentCount = currentData?.filteredMatters.count;
          const currentMatters = currentData?.filteredMatters.matters;

          return {
            filteredMatters: {
              __typename: "MatterPage" as const,
              count: currentCount + 1,
              matters: [
                {
                  ...createdMatter!.data!.createMatter,
                  __typename: "BaseMatterResult" as const,
                  brand: null,
                  isParent: false,
                  collaborators: [],
                },
                ...(currentMatters || []),
              ],
            },
          };
        }
      );
      if (createdMatter.data.createMatter.externalMatterId) {
        cache.modify({
          id: `ExternalMatter:${createdMatter.data.createMatter.externalMatterId}`,
          fields: {
            status() {
              return (
                createdMatter.data?.createMatter.externalMatter?.status ??
                ExternalMatterStatus.Unassigned
              );
            },
            matter() {
              return {
                id: createdMatter.data?.createMatter.id,
              };
            },
          },
        });
      }
    },
  });

  const [selectedClient, setSelectedClient] = useState<ClientValue | undefined | null>(client);

  useEffect(() => {
    setSelectedClient(client);
  }, [client]);

  const clientFilterData = useFilteredClientsLazy(10);

  const [createClient] = useMutation<createClient, createClientVariables>(CREATE_CLIENT, {
    update: (cache, mutationResult) => {
      const newClient = mutationResult.data?.createClient;
      if (!newClient) {
        return;
      }

      const data = cache.readQuery<FindFilteredClients, FindFilteredClientsVariables>({
        query: FILTERED_CLIENTS_QUERY,
        variables: clientFilterData?.queryVars,
      });

      cache.writeQuery<FindFilteredClients, FindFilteredClientsVariables>({
        query: FILTERED_CLIENTS_QUERY,
        variables: {
          ...clientFilterData?.queryVars,
        },
        data: {
          filteredClients: {
            clients: [...(data?.filteredClients?.clients || []), newClient],
            total: (data?.filteredClients?.total ?? 0) + 1,
            __typename: "ClientResult",
          },
        },
      });
      if (newClient?.name) {
        setSelectedClient({
          ...newClient,
        });
        clientFilterData.search(newClient?.name);
      }
    },
  });
  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnits>(
    ORGANISATIONAL_UNITS_QUERY
  );

  const externalMatterData = useExternalMattersQueryLazy();
  const { settings } = useSettings();

  return (
    <CreateMatter
      parentMatterId={parentMatterId}
      labelOveride={labelOveride}
      createMultiDiscMatter={({ title, phases, clientId }) => {
        createMultiDiscMatter({
          variables: {
            createInput: title,
            phases,
            clientId,
          },
          onCompleted: (data) => {
            if (!data.createParentMatter) return;
            return navigate(`/parent/${data.createParentMatter.id}`, {
              replace: true,
            });
          },
        });
      }}
      renderMultiDisc={({ form }) => (
        <CreateMultiDisc
          onClientSave={
            ((values: any) => {
              createClient({ variables: { data: values as any } });
            }) as OnClientSave
          }
          selectedClient={selectedClient}
          clientFilterData={clientFilterData as ClientFilterData}
          form={form}
        />
      )}
      onCreate={({ templateId, practiceAreaId, parentPhaseId, ...values }: any) => {
        if (templateId === undefined) {
          createMatter({
            variables: {
              createFromOptions: null,
              parentId: parentMatterId || null,
              parentPhaseId: parentPhaseId || null,
              data: {
                ...(practiceAreaId && { organisationalUnit: { connect: { id: practiceAreaId } } }),
                ...(values?.clientId && {
                  client: { connect: { id: values.clientId } },
                }),
                ...(values.externalMatterId && {
                  externalMatter: { connect: { id: values.externalMatterId } },
                }),
                ...(values?.leadFeeEarnerId && {
                  feeEarner: { connect: { id: values.leadFeeEarnerId } },
                }),
                title: values.title,
                status: values.status,
                pricingType: values.pricingType,
                pricingLevel: values.pricingLevel,
                pricingTarget: values.pricingTarget,
                ...(values.matterCurrencyId && {
                  currency: {
                    connect: {
                      id: values.matterCurrencyId,
                    },
                  },
                }),
                ...(values.officeLocationId && {
                  officeLocation: {
                    connect: {
                      id: values.officeLocationId,
                    },
                  },
                }),
                confidential: values.confidential,
              },
            },
          });
        } else {
          createMatter({
            variables: {
              createFromOptions: {
                templateId: templateId,
              },
              parentId: parentMatterId || null,
              parentPhaseId: parentPhaseId || null,
              data: {
                ...(practiceAreaId && { organisationalUnit: { connect: { id: practiceAreaId } } }),
                ...(values?.clientId && {
                  client: { connect: { id: values.clientId } },
                }),
                ...(values?.leadFeeEarnerId && {
                  feeEarner: { connect: { id: values.leadFeeEarnerId } },
                }),
                ...(values.externalMatterId && {
                  externalMatter: { connect: { id: values.externalMatterId } },
                }),
                title: values.title,
                status: values.status,
                pricingType: values.pricingType,
                pricingLevel: values.pricingLevel,
                pricingTarget: values.pricingTarget,
                ...(values.matterCurrencyId && {
                  currency: {
                    connect: {
                      id: values.matterCurrencyId,
                    },
                  },
                }),
                ...(values.officeLocationId && {
                  officeLocation: {
                    connect: {
                      id: values.officeLocationId,
                    },
                  },
                }),
                confidential: values.confidential,
              },
            },
          });
        }
      }}
      onCancel={() => {
        setSelectedClient(null);
      }}
      loading={loadingCreateMatter}
      renderStartingPoint={({ form }) => (
        <StartingPointSelection showMultiDisc={!parentMatterId} form={form} />
      )}
      renderMatterDetailsForm={({ form }: any) => {
        return (
          <MatterCreationOrEditingForm
            client={selectedClient}
            setCurrentClient={(client) => {
              setSelectedClient(client);
            }}
            template={"matter"}
            parentMatterId={parentMatterId}
            form={form}
            externalMatterData={externalMatterData}
            pmsName={settings?.organisationPmsName || ""}
            practiceAreas={organisationalUnitData?.organisationalUnits || []}
            onClientSave={
              ((values: any) => {
                createClient({ variables: { data: values as any } });
              }) as OnClientSave
            }
            clientFilterData={clientFilterData as ClientFilterData}
            feeEarnersCascaderRender={() => <FeeEarnerCascaderContainer showSearch />}
            mode={MatterFormMode.Create}
            allowChangeCurrency={true}
          />
        );
      }}
      renderSelectTemplateForm={({ onSelect, libraryMatterType, form }) => {
        const startingPoint = form.getFieldValue("startingPoint") as MatterStartingOptions;
        const searchType =
          startingPoint === MatterStartingOptions.HistoricalMatter
            ? "Historial Matter"
            : TEMPLATE_LABEL;
        return (
          <TemplateSelectionForm searchType={searchType}>
            <Tabs.TabPane tab={`${PA_LABEL} Specific`} key="1">
              <PracticeAreaTemplateTabContainer
                onSelect={onSelect}
                libraryMatterType={libraryMatterType}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="Client Specific" key="2">
              <ClientTemplateTabContainer
                onSelect={onSelect}
                libraryMatterType={libraryMatterType}
              />
            </Tabs.TabPane>
          </TemplateSelectionForm>
        );
      }}
    />
  );
};
